import React, { useState, useEffect } from 'react';
import { db, auth, provider, storage } from './firebase'; 
import { collection, onSnapshot, addDoc, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, EffectCoverflow, Pagination } from 'swiper/modules'; 
import { motion, AnimatePresence } from 'framer-motion';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-coverflow'; 
import 'swiper/css/pagination';
import './App.css';

// --- 🔒 ТУК ЗАДАВАМЕ КОЙ Е АДМИНЪТ ---
const ADMIN_EMAILS = ["app.karlovo@gmail.com"];

// --- КАТЕГОРИИ (Новата подредба) ---
const CATEGORIES = [
  { bg: "Закуски", en: "Breakfast" },
  { bg: "Бургери и сандвичи", en: "Burgers & Sandwiches" },
  { bg: "Салати", en: "Salads" },
  { bg: "Предястия", en: "Starters" },
  { bg: "Ризото и Паста", en: "Risotto & Pasta" },
  { bg: "Основни Ястия", en: "Main Courses" },
  { bg: "Барбекю", en: "BBQ" },
  { bg: "Пица", en: "Pizza" },
  { bg: "Суши и Азиатски", en: "Sushi & Asian" },
  { bg: "Десерти", en: "Desserts" }
];

// --- ЕЗИЦИ ---
const T = {
  BG: {
    specialTab: "★ СПЕЦИАЛНИ",
    menuTab: "МЕНЮ",
    barTab: "🍹 БАР",
    specialsTitle: "НАШИТЕ СПЕЦИАЛНИ ПРЕДЛОЖЕНИЯ",
    noSpecials: "Няма специални предложения в момента.",
    resBtn: "РЕЗЕРВАЦИЯ",
    orderBtn: "ПОРЪЧКА",
    heroTitle: "RESTAURANT KARLOVO",
    heroSub: "FINE DINING & GARDEN",
    hidden: "(СКРИТО)"
  },
  EN: {
    specialTab: "★ SPECIALS",
    menuTab: "MENU",
    barTab: "🍹 BAR",
    specialsTitle: "OUR SPECIAL OFFERS",
    noSpecials: "No special offers at the moment.",
    resBtn: "RESERVATION",
    orderBtn: "ORDER",
    heroTitle: "RESTAURANT KARLOVO",
    heroSub: "FINE DINING & GARDEN",
    hidden: "(HIDDEN)"
  }
};

// --- ФУНКЦИИ ЗА ЦЕНИТЕ ---
const formatEuro = (price) => {
  if (!price) return "0.00";
  const num = parseFloat(price.toString().replace(',', '.'));
  if (isNaN(num)) return price;
  return num.toFixed(2);
};

function App() {
  const [user, setUser] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [banners, setBanners] = useState([]);
  
  const [activeTab, setActiveTab] = useState('special'); 
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0].bg);
  const [lang, setLang] = useState('BG');

  const [itemForm, setItemForm] = useState({ title: '', titleEn: '', price: '', description: '', descriptionEn: '', category: 'Закуски' });
  const [itemFile, setItemFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // 🔒 ПРОВЕРКА ДАЛИ ЛОГНАТИЯТ Е АДМИН
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);

  useEffect(() => {
    onAuthStateChanged(auth, u => {
        if (u) {
            // Ако някой се логне, но НЕ е в списъка с админи -> изхвърли го веднага!
            if (!ADMIN_EMAILS.includes(u.email)) {
                alert("Нямате администраторски права за този сайт!");
                signOut(auth);
                setUser(null);
            } else {
                setUser(u); // Ако е app.karlovo, пусни го
            }
        } else {
            setUser(null);
        }
    });

    const unsubMenu = onSnapshot(collection(db, "menu_items"), (snap) => {
      setMenuItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubBanners = onSnapshot(collection(db, "promo_banners"), (snap) => {
      setBanners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubMenu(); unsubBanners(); };
  }, []);

  const visibleItems = isAdmin ? menuItems : menuItems.filter(item => !item.isHidden);
  const specialItems = visibleItems.filter(i => i.isSpecial);
  
  const filtered = visibleItems
    .filter(item => item.category === selectedCategory)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const moveItem = async (index, direction) => {
      if (!isAdmin) return; // 🔒 
      const itemsToUpdate = [...filtered]; 
      
      if (direction === 'up' && index > 0) {
        [itemsToUpdate[index], itemsToUpdate[index - 1]] = [itemsToUpdate[index - 1], itemsToUpdate[index]];
      } else if (direction === 'down' && index < itemsToUpdate.length - 1) {
         [itemsToUpdate[index], itemsToUpdate[index + 1]] = [itemsToUpdate[index + 1], itemsToUpdate[index]];
      } else {
          return;
      }

      itemsToUpdate.forEach((item, idx) => {
         updateDoc(doc(db, "menu_items", item.id), { order: idx });
      });
  };

  const handleSaveItem = async () => {
    if (!isAdmin) return; // 🔒
    if (!itemForm.title || !itemForm.price) return alert("Липсва Име (BG) или Цена!");
    let url = itemForm.imageUrl || "";
    if (itemFile) {
      const imgRef = ref(storage, `menu/${Date.now()}_${itemFile.name}`);
      await uploadBytes(imgRef, itemFile);
      url = await getDownloadURL(imgRef);
    }
    
    const nextOrder = menuItems.length + 100; 

    if (editingId) {
      await updateDoc(doc(db, "menu_items", editingId), { ...itemForm, imageUrl: url });
      setEditingId(null);
    } else {
      await addDoc(collection(db, "menu_items"), { ...itemForm, imageUrl: url, isSpecial: false, isHidden: false, order: nextOrder });
    }
    
    setItemForm({ title: '', titleEn: '', price: '', description: '', descriptionEn: '', category: 'Закуски' });
    setItemFile(null);
    alert("Запазено!");
  };

  const handleUploadBanner = async () => {
    if (!isAdmin) return; // 🔒
    if (!bannerFile) { alert("Избери файл!"); return; }
    setIsUploading(true); 
    try {
      const imgRef = ref(storage, `banners/${Date.now()}_${bannerFile.name}`);
      await uploadBytes(imgRef, bannerFile);
      const url = await getDownloadURL(imgRef);
      await addDoc(collection(db, "promo_banners"), { imageUrl: url });
      setBannerFile(null);
      alert("Качено!");
    } catch (error) { alert("Грешка: " + error.message); }
    setIsUploading(false); 
  };

  const startEdit = (item) => {
    if (!isAdmin) return; // 🔒
    setEditingId(item.id);
    setItemForm({
      title: item.title || '',
      titleEn: item.titleEn || '',
      price: item.price || '',
      description: item.description || '',
      descriptionEn: item.descriptionEn || '',
      category: item.category || 'Закуски', 
      imageUrl: item.imageUrl || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleVisibility = async (item) => {
      if (!isAdmin) return; // 🔒
      await updateDoc(doc(db, "menu_items", item.id), { isHidden: !item.isHidden });
  };

  const displayTitle = (item) => lang === 'EN' && item.titleEn ? item.titleEn : item.title;
  const displayDesc = (item) => lang === 'EN' && item.descriptionEn ? item.descriptionEn : item.description;

  return (
    <div className="App">
      
      {/* --- АДМИН ПАНЕЛ --- */}
      {isAdmin && (
        <div className="admin-dashboard-top">
          <div className="admin-header">
            <h3>АДМИН ПАНЕЛ</h3>
            <button onClick={() => signOut(auth)} className="btn-logout">ИЗХОД</button>
          </div>
          <div className="admin-row">
            <input placeholder="Име (BG)" value={itemForm.title} onChange={e=>setItemForm({...itemForm, title: e.target.value})} />
            <input placeholder="Name (EN)" value={itemForm.titleEn} onChange={e=>setItemForm({...itemForm, titleEn: e.target.value})} />
            <input placeholder="Цена (ВЪВЕДИ В ЕВРО)" value={itemForm.price} onChange={e=>setItemForm({...itemForm, price: e.target.value})} />
          </div>
          <div className="admin-row">
            <input placeholder="Описание (BG)" value={itemForm.description} onChange={e=>setItemForm({...itemForm, description: e.target.value})} />
            <input placeholder="Description (EN)" value={itemForm.descriptionEn} onChange={e=>setItemForm({...itemForm, descriptionEn: e.target.value})} />
            
            <select value={itemForm.category} onChange={e=>setItemForm({...itemForm, category: e.target.value})} style={{padding: '10px', background: '#000', color: '#fff', border: '1px solid #444', flex: 1}}>
               {CATEGORIES.map(cat => (
                   <option key={cat.bg} value={cat.bg}>{cat.bg}</option>
               ))}
            </select>
          </div>
          <div className="admin-row">
            <input type="file" onChange={e=>setItemFile(e.target.files[0])} style={{flex: 1, padding: '5px', color: 'white'}} />
            <button className="btn-save" onClick={handleSaveItem}>
                {editingId ? "ЗАПАЗИ ПРОМЕНИТЕ" : "ДОБАВИ ЯСТИЕ"}
            </button>
          </div>
          <div className="admin-row" style={{borderTop:'1px solid #444', paddingTop:'10px'}}>
             <h4 style={{color:'white', margin:'0 10px 0 0'}}>Банер (Фон / MP4):</h4>
             <input type="file" accept="image/*,video/mp4" onChange={e=>setBannerFile(e.target.files[0])} style={{color: 'white'}} />
             <button className="btn-save" style={{background: isUploading ? '#888' : '#2196F3', flex: 'none', padding: '10px 30px', color: 'white', cursor: isUploading ? 'not-allowed' : 'pointer'}} onClick={handleUploadBanner} disabled={isUploading}>
                 {isUploading ? "КАЧВАНЕ..." : "КАЧИ БАНЕР"}
             </button>
          </div>
        </div>
      )}

      {/* --- HERO СЛАЙДЕР --- */}
      <section className="hero-section">
        <Swiper modules={[Autoplay, EffectFade]} effect={'fade'} autoplay={{ delay: 5000 }} loop className="mySwiper">
           {banners.length > 0 ? banners.map(b => (
             <SwiperSlide key={b.id}>
               {b.imageUrl && b.imageUrl.toLowerCase().includes('.mp4') ? (
                   <video autoPlay loop muted playsInline className="hero-video-bg">
                       <source src={b.imageUrl} type="video/mp4" />
                   </video>
               ) : (
                   <div className="slide-bg" style={{ backgroundImage: `url(${b.imageUrl})` }}></div>
               )}
               {isAdmin && <button className="del-banner-btn" onClick={() => deleteDoc(doc(db, "promo_banners", b.id))}>ИЗТРИЙ</button>}
             </SwiperSlide>
           )) : <SwiperSlide><div className="slide-bg" style={{background:'#333'}}></div></SwiperSlide>}
        </Swiper>
        <div className="hero-content">
          <h1 className="hero-title">{T[lang].heroTitle}</h1>
          <p className="hero-subtitle">{T[lang].heroSub}</p>
        </div>
      </section>

      {/* --- НАВИГАЦИЯ --- */}
      <div className="sticky-navigation position-relative">
        <div className="lang-switcher">
            <button className={`lang-btn ${lang === 'BG' ? 'active' : ''}`} onClick={() => setLang('BG')}>BG</button>
            <span className="lang-divider">|</span>
            <button className={`lang-btn ${lang === 'EN' ? 'active' : ''}`} onClick={() => setLang('EN')}>EN</button>
        </div>

        <div className="main-tabs">
          <button className={activeTab === 'special' ? 'active' : ''} onClick={() => setActiveTab('special')}>{T[lang].specialTab}</button>
          <button className={activeTab === 'main' ? 'active' : ''} onClick={() => setActiveTab('main')}>{T[lang].menuTab}</button>
          <a href="https://bar-dahad-karlovo-2026.web.app/" target="_blank" rel="noopener noreferrer" className="bar-link-tab">{T[lang].barTab}</a>
        </div>
        
        {activeTab === 'main' && (
          <div className="sub-categories">
            {CATEGORIES.map(cat => (
              <button key={cat.bg} className={selectedCategory === cat.bg ? 'active' : ''} onClick={() => setSelectedCategory(cat.bg)}>
                  {lang === 'EN' ? cat.en : cat.bg}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* --- СЪДЪРЖАНИЕ --- */}
      <main className="menu-container">
        
        {activeTab === 'special' && (
          <div className="special-slider-container">
            <h2 className="section-title">{T[lang].specialsTitle}</h2>
            {specialItems.length > 0 ? (
              <Swiper
                effect={'coverflow'} grabCursor={true} centeredSlides={true} slidesPerView={'auto'} 
                coverflowEffect={{ rotate: 50, stretch: 0, depth: 100, modifier: 1, slideShadows: true }}
                autoplay={{ delay: 2500, disableOnInteraction: false }}
                pagination={true} modules={[EffectCoverflow, Pagination, Autoplay]} className="swiper-roulette"
              >
                {specialItems.map(item => (
                  <SwiperSlide key={item.id} className="roulette-slide">
                    <div className="special-card" style={{ opacity: item.isHidden ? 0.4 : 1, filter: item.isHidden ? 'grayscale(100%)' : 'none' }}>
                       <img src={item.imageUrl} alt={displayTitle(item)} />
                       <div className="special-info">
                         <h3>{displayTitle(item)}</h3>
                         <p>{displayDesc(item)}</p>
                         <span className="price">{formatEuro(item.price)} €</span>
                       </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : <p style={{textAlign:'center', color:'#888'}}>{T[lang].noSpecials}</p>}
          </div>
        )}

        {activeTab === 'main' && (
          <div className="row">
            <AnimatePresence>
              {filtered.map((item, idx) => (
                <div key={item.id} className="col">
                  <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="larana-card" style={{ opacity: item.isHidden ? 0.4 : 1, filter: item.isHidden ? 'grayscale(80%)' : 'none' }}>
                    
                    {/* --- АДМИН БУТОНИ --- */}
                    {isAdmin && (
                      <div className="admin-controls" style={{
                          background: 'rgba(0,0,0,0.8)',
                          padding: '5px',
                          borderRadius: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                      }}>
                        <button onClick={() => moveItem(idx, 'up')} style={{background: 'white', border:'none', cursor:'pointer', padding:'5px', fontSize:'16px'}}>⬆️</button>
                        <button onClick={() => moveItem(idx, 'down')} style={{background: 'white', border:'none', cursor:'pointer', padding:'5px', fontSize:'16px'}}>⬇️</button>
                        
                        <div style={{width:'1px', height:'20px', background:'white', margin:'0 5px'}}></div>

                        <button className="btn-icon edit" title="Редактирай" onClick={() => startEdit(item)}>✎</button>
                        <button className="btn-icon star" title="В Специални" onClick={() => updateDoc(doc(db, "menu_items", item.id), {isSpecial: !item.isSpecial})}>
                          {item.isSpecial ? "★" : "☆"}
                        </button>
                        <button className="btn-icon" style={{color: '#ffc107', background: 'rgba(0,0,0,0.6)'}} title="Скрий/Покажи" onClick={() => toggleVisibility(item)}>{item.isHidden ? "🙈" : "👁️"}</button>
                        <button className="btn-icon del" title="Изтрий" onClick={() => deleteDoc(doc(db, "menu_items", item.id))}>X</button>
                      </div>
                    )}

                    <div className="card-img-container">
                      <img src={item.imageUrl || 'https://via.placeholder.com/300'} alt={displayTitle(item)} />
                    </div>
                    <div className="card-info">
                      <h3>{displayTitle(item)} {item.isHidden && <span style={{color: 'red', fontSize:'0.8rem'}}>{T[lang].hidden}</span>}</h3>
                      <p className="desc-text">{displayDesc(item)}</p>
                      <span className="price-tag">{formatEuro(item.price)} €</span>
                    </div>
                  </motion.div>
                </div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {!isAdmin && (
        <div className="footer-btns">
          <a href="tel:+359884517950" className="f-btn call">{T[lang].resBtn}</a>
          <a href="tel:+359897923503" className="f-btn order">{T[lang].orderBtn}</a>
        </div>
      )}
      
      {/* Малко скрито бутонче за теб да се логнеш */}
      {!isAdmin && <button onClick={() => signInWithPopup(auth, provider)} className="admin-login-link">Admin</button>}
    </div>
  );
}

export default App;